import { chakra, Flex } from '@chakra-ui/react';
import React from 'react';

import useApiQuery from 'lib/api/useApiQuery';
import dayjs from 'lib/date/dayjs';
import { HOMEPAGE_STATS } from 'stubs/stats';
import { Skeleton } from 'toolkit/chakra/skeleton';
import GasInfoTooltip from 'ui/shared/gas/GasInfoTooltip';
import GasPrice from 'ui/shared/gas/GasPrice';

interface Props {
  compact?: boolean;
}

const HeaderGasPrice = ({ compact }: Props) => {
  const { data, isPlaceholderData, isError, refetch, dataUpdatedAt } = useApiQuery('general:stats', {
    queryOptions: {
      placeholderData: HOMEPAGE_STATS,
      refetchOnMount: false,
    },
  });

  React.useEffect(() => {
    if (isPlaceholderData || !data?.gas_price_updated_at) {
      return;
    }

    const endDate = dayjs(dataUpdatedAt).add(data.gas_prices_update_in, 'ms');
    const timeout = endDate.diff(dayjs(), 'ms');

    if (timeout <= 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      refetch();
    }, timeout);

    return () => window.clearTimeout(timeoutId);
  }, [ isPlaceholderData, data?.gas_price_updated_at, data?.gas_prices_update_in, dataUpdatedAt, refetch ]);

  if (isError || !data?.gas_prices || data.gas_prices.average === null) {
    return null;
  }

  return (
    <Skeleton loading={ isPlaceholderData } display="inline-flex" flexShrink={ 0 }>
      <GasInfoTooltip data={ data } dataUpdatedAt={ dataUpdatedAt } placement="bottom-end">
        <Flex
          alignItems="center"
          gap={ 1.5 }
          minH={ compact ? 9 : 10 }
          px={ compact ? 2 : 3 }
          color="blue.900"
          bgColor="blue.50"
          borderColor="blue.100"
          borderWidth="1px"
          borderRadius="lg"
          fontSize={ compact ? 'xs' : 'sm' }
          fontWeight={ 600 }
          whiteSpace="nowrap"
        >
          <chakra.span color="text.secondary">گس</chakra.span>
          <GasPrice data={ data.gas_prices.average }/>
        </Flex>
      </GasInfoTooltip>
    </Skeleton>
  );
};

export default React.memo(HeaderGasPrice);
